import { env } from '@/env.mjs';
import { MediaPlacement } from '@prisma/client';

// CDN Configuration
export const MEDIA_CDN = env.NEXT_PUBLIC_MEDIA_CDN as string;

interface MediaConfig {
  maxSize: number;
  acceptedTypes: Record<string, string[]>;
  allowMultiple: boolean;
  dimensions?: {
    minWidth?: number;
    minHeight?: number;
    maxWidth?: number;
    maxHeight?: number;
  };
}

const BASE_ACCEPTED_TYPES: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'image/svg+xml': ['.svg'],
};

const VIDEO_TYPES: Record<string, string[]> = {
  'video/mp4': ['.mp4'],
  'video/webm': ['.webm'],
  'video/ogg': ['.ogv'],
};

// Size constants in bytes
const SIZE = {
  KB: 1024,
  MB: 1024 * 1024,
  GB: 1024 * 1024 * 1024
} as const;

type MediaConfigs = {
  [key in MediaPlacement]?: MediaConfig;
};

const configs: MediaConfigs = {
  [MediaPlacement.AVATAR]: {
    maxSize: 19 * SIZE.MB,
    acceptedTypes: BASE_ACCEPTED_TYPES,
    allowMultiple: false,
    dimensions: {
      minWidth: 400,
      minHeight: 400
    },
  },
  [MediaPlacement.FEATURED_MEDIA]: {
    maxSize: 100 * SIZE.MB,
    acceptedTypes: {
      ...BASE_ACCEPTED_TYPES,
      ...VIDEO_TYPES,
    },
    allowMultiple: false,
    dimensions: {
      minWidth: 1200,
      minHeight: 630
    },
  },
  [MediaPlacement.PROJECT_MEDIA]: {
    maxSize: 256 * SIZE.MB,
    acceptedTypes: {
      ...BASE_ACCEPTED_TYPES,
      ...VIDEO_TYPES,
    },
    allowMultiple: true,
    dimensions: {
      minWidth: 800,
      minHeight: 600
    },
  },
  [MediaPlacement.USER_PROFILE_MEDIA]: {
    maxSize: 100 * SIZE.MB,
    acceptedTypes: BASE_ACCEPTED_TYPES,
    allowMultiple: true,
    dimensions: {
      minWidth: 400,
      minHeight: 400
    },
  },
  [MediaPlacement.ARTICLE_MEDIA]: {
    maxSize: 256 * SIZE.MB,
    acceptedTypes: BASE_ACCEPTED_TYPES,
    allowMultiple: true,
    dimensions: {
      minWidth: 800,
      minHeight: 600
    },
  },
};

export function getMediaConfig(placement: MediaPlacement): MediaConfig {
  const config = configs[placement];
  if (!config) {
    // Return a default config if placement is not explicitly configured
    return {
      maxSize: 20 * SIZE.MB,
      acceptedTypes: BASE_ACCEPTED_TYPES,
      allowMultiple: false,
      dimensions: {
        minWidth: 800,
        minHeight: 600,
        maxWidth: 2560,
        maxHeight: 1440
      },
    };
  }
  return config;
}

export function isDefaultImage(file: File | Blob): boolean {
  return (
    file instanceof Blob &&
    (file.type === 'image/svg+xml' || file.type === 'image/png') &&
    (file instanceof File ? file.name.startsWith('default-') : false)
  );
}

export function validateMediaDimensions(
  file: File,
  config: MediaConfig
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    if (!config.dimensions) {
      resolve(true);
      return;
    }

    const img = new Image();
    img.onload = () => {
      const dimensions = config.dimensions;
      if (!dimensions) {
        resolve(true);
        return;
      }

      const { minWidth, minHeight, maxWidth, maxHeight } = dimensions;

      // Check minimum dimensions if specified
      const isMinSizeValid = !minWidth || !minHeight ||
        (img.width >= minWidth && img.height >= minHeight);

      // Check maximum dimensions if specified
      const isMaxSizeValid = !maxWidth || !maxHeight ||
        (img.width <= maxWidth && img.height <= maxHeight);

      resolve(isMinSizeValid && isMaxSizeValid);
    };
    img.onerror = () => reject(new Error('Failed to load image for validation'));
    img.src = URL.createObjectURL(file);
  });
}

// Helper functions
export function isVideoType(mimeType: string): boolean {
  return mimeType.startsWith('video/');
}

export function isImageType(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

export function validateFileType(file: File, config: MediaConfig): boolean {
  const { acceptedTypes } = config;

  // Check if the file type matches any of the accepted types
  return Object.entries(acceptedTypes).some(([mimeType, extensions]) => {
    // Handle wildcard mime types (e.g., 'image/*')
    if (mimeType.endsWith('/*')) {
      const baseType = mimeType.split('/')[0];
      return file.type.startsWith(`${baseType}/`);
    }

    // Handle exact mime type match
    if (file.type === mimeType) {
      return true;
    }

    // Handle file extension match only if the base type matches
    const [fileBaseType] = file.type.split('/');
    const [mimeBaseType] = mimeType.split('/');
    if (fileBaseType === mimeBaseType) {
      return extensions.some(ext =>
        file.name.toLowerCase().endsWith(ext.toLowerCase())
      );
    }

    return false;
  });
}

export function validateFileSize(file: File, config: MediaConfig): boolean {
  return file.size <= config.maxSize;
}

export function getAcceptedFileTypes(config: MediaConfig): string {
  return Object.entries(config.acceptedTypes)
    .map(([, extensions]) => extensions.join(', '))
    .join(', ');
}

